/**
 * Docked-companion layout (owner directive 2026-08-24, round 2).
 *
 * THE one description of "the chat is on screen as the docked right rail, so
 * whatever else is open must share the viewport with it instead of fighting
 * it". Round 1 taught the PAGE to reflow beside the rail (App's
 * RoutedContentShell); this module lifts that knowledge out of App.tsx so
 * every companion surface derives the same geometry from the same numbers:
 *
 *   • routed page content            — App.tsx RoutedContentShell
 *   • the file-preview modal         — FilePreview/FilePreviewOverlay
 *   • floating preview windows       — FilePreview/windowGeometry (JS rects)
 *
 * Two halves, deliberately:
 *   1. GEOMETRY (pure): how much of the screen's right edge the rail owns,
 *      as a CSS length for stylesheets and as pixels for JS rect math.
 *   2. THE LIVE BIT (React): is that rail actually on screen right now?
 *      Read from the <html data-prest0-chat-mode="docked"> stamp the one
 *      persistent chat mount maintains (chatModeSignal.ts) — so a companion
 *      needs NO chat state, no context, and no provider. It works on any
 *      page, inside or outside the chat's React tree, which is exactly what
 *      the preview surfaces need (they are body portals mounted app-level).
 *
 * The attribute already means "the docked pane is VISIBLE right now" (open,
 * docked, not fullscreen, not hidden) — see chatModeSignal.ts. Companions
 * inherit that meaning for free: a closed-but-docked pill never reserves
 * space, and a mode change while a companion is open re-lays it out live.
 */
import { useSyncExternalStore } from 'react';
import { CHAT_LAYOUT } from './types';
import { CHAT_MODE_ATTRIBUTE, CHAT_MODE_DOCKED } from './chatModeSignal';

/**
 * The docked pane's own outer inset from the viewport's right edge.
 * styles.ts hardwires `right: 24px` on the docked branch (pinned by
 * dockedRightConvention.test.ts), so this mirrors that literal.
 */
export const DOCKED_RAIL_INSET_PX = 24;

/**
 * Width of screen the docked rail owns end to end: the pane, its outer
 * inset, and the gutter of clear air a companion must leave beside it.
 * 24 | companion | 24 | pane | 24 — an even three-column rhythm.
 */
export const DOCKED_RAIL_FOOTPRINT_PX =
  CHAT_LAYOUT.DOCKED_WIDTH_PX + DOCKED_RAIL_INSET_PX + CHAT_LAYOUT.DOCKED_GUTTER;

/**
 * The same footprint as a CSS length. Derived from CHAT_LAYOUT.DOCKED_WIDTH
 * (the min() string) rather than the number, so a quirky viewport shrinks
 * the reservation exactly as it shrinks the pane.
 */
export const DOCKED_RAIL_FOOTPRINT_CSS =
  `calc(${CHAT_LAYOUT.DOCKED_WIDTH} + ${DOCKED_RAIL_INSET_PX + CHAT_LAYOUT.DOCKED_GUTTER}px)`;

/**
 * Below this viewport width the docked pane becomes a near-full-width
 * overlay (its own <=900px rules in styles.ts), so there IS no remaining
 * space to share — every companion stands down and takes the full screen
 * back. One number, so the CSS media queries and the JS rect math can never
 * disagree about where the split view ends.
 */
export const DOCKED_COMPANION_MIN_VIEWPORT_PX = 901;

/** Pure reader for the root-level docked stamp. SSR/jsdom safe. */
export function readChatDockedAttribute(
  doc: Document | undefined = typeof document !== 'undefined' ? document : undefined,
): boolean {
  return doc?.documentElement?.getAttribute(CHAT_MODE_ATTRIBUTE) === CHAT_MODE_DOCKED;
}

/**
 * Pure decision: is the docked companion split active right now? Both halves
 * must hold — the rail is on screen AND the viewport is wide enough to split.
 */
export function isDockedCompanionActive(args: {
  docked: boolean;
  viewportWidth: number;
}): boolean {
  return args.docked && args.viewportWidth >= DOCKED_COMPANION_MIN_VIEWPORT_PX;
}

/**
 * Pixels of the viewport's right edge a companion must keep clear. Zero
 * whenever the split is not active, so callers can add it unconditionally.
 */
export function companionReservedRightPx(args: {
  docked: boolean;
  viewportWidth: number;
}): number {
  if (!isDockedCompanionActive(args)) return 0;
  // Mirror the CSS min(): a narrow-but-still-splitting viewport shrinks the
  // pane, and the reservation shrinks with it.
  const paneWidth = Math.min(CHAT_LAYOUT.DOCKED_WIDTH_PX, args.viewportWidth - 48);
  return paneWidth + DOCKED_RAIL_INSET_PX + CHAT_LAYOUT.DOCKED_GUTTER;
}

/* ── The live bit ─────────────────────────────────────────────────────── */

type Listener = () => void;

function subscribe(onStoreChange: Listener): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => undefined;
  // The stamp is an ATTRIBUTE mutation on <html>, not an event — observe it.
  // Filtered to the one attribute so unrelated root-class churn costs nothing.
  const observer = typeof MutationObserver === 'function'
    ? new MutationObserver(onStoreChange)
    : null;
  observer?.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [CHAT_MODE_ATTRIBUTE],
  });
  // Crossing the split's viewport floor changes the answer too.
  window.addEventListener('resize', onStoreChange);
  return () => {
    observer?.disconnect();
    window.removeEventListener('resize', onStoreChange);
  };
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined') return false;
  return isDockedCompanionActive({
    docked: readChatDockedAttribute(),
    viewportWidth: window.innerWidth,
  });
}

/** Server/SSR snapshot: never split — the client corrects on hydration. */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * "Is the chat currently the docked right rail, with room to share?"
 *
 * Returns a plain boolean, so a companion can key both its layout and its
 * behavior off it (the preview modal, for instance, drops its page-scroll
 * lock in split view — the page beside it is meant to stay usable).
 */
export function useIsChatDockedCompanion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
